// ✅ ALTERED ONLY FOR THEME (match your “above theme” / ReportStudentMarks)
// - Added page wrapper (Box) with BG #F2F2F2 (like other pages)
// - Kept root paper + orange header bar
// - Sidebar uses sectionSx with left orange accent stripe
// - Calendar card uses surfaceSx; header row in calendar is BLUE
// - Inputs: blue border + orange focus + orange focused label
// - Buttons: primary orange, outlined blue (consistent)
// - Dialog titles match blue heading, actions buttons consistent
// - Replaced inline <input type="color"> styling with MUI-styled Box wrapper (still native input; same logic)

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Button,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment-timezone";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { RRule } from "rrule";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { API_URL } from "./config";

const DnDCalendar = withDragAndDrop(Calendar);

// Custom event component that intercepts right-click to trigger recurrence editing
const CustomEvent = ({ event, title, onEditRecurrence, ...props }) => {
  const handleContextMenu = (e) => {
    e.preventDefault();
    if (onEditRecurrence) onEditRecurrence(event);
  };
  return (
    <div onContextMenu={handleContextMenu} {...props}>
      {title}
    </div>
  );
};

const ClassScheduler = () => {
  // ===== Theme constants (same as your other pages) =====
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  // ===== Calendar local CSS overrides (now matches your theme better) =====
  const calendarCss = `
    .rbc-toolbar{
      padding: 8px 10px !important;
      gap: 8px !important;
      flex-wrap: wrap !important;
    }
    .rbc-toolbar button{
      border-radius: 10px !important;
      border: 1px solid rgba(25,118,210,0.25) !important;
      background: white !important;
      color: rgba(25,118,210,0.95) !important;
      font-weight: 800 !important;
      padding: 6px 10px !important;
    }
    .rbc-toolbar button.rbc-active{
      background: rgba(240,120,40,0.12) !important;
      border-color: rgba(240,120,40,0.60) !important;
      color: rgba(240,120,40,1) !important;
    }
    .rbc-toolbar button:hover{
      background: rgba(25,118,210,0.05) !important;
      border-color: rgba(25,118,210,0.45) !important;
    }
    .rbc-time-view, .rbc-month-view{
      border: 1px solid rgba(25,118,210,0.18) !important;
      border-radius: 12px !important;
      overflow: hidden !important;
      background: white !important;
    }
    .rbc-header{
      background: rgba(25,118,210,0.95) !important;
      color: white !important;
      font-weight: 900 !important;
      padding: 8px 6px !important;
      border-bottom: 1px solid rgba(255,255,255,0.18) !important;
    }
    .rbc-allday-cell{
      background: rgba(25,118,210,0.06) !important;
    }
    .rbc-time-header-content{
      border-left: 1px solid rgba(25,118,210,0.15) !important;
    }
    .rbc-time-content{
      border-top: 1px solid rgba(25,118,210,0.15) !important;
    }
    .rbc-timeslot-group{
      border-bottom: 1px solid rgba(25,118,210,0.08) !important;
    }
    .rbc-day-slot .rbc-time-slot{
      border-top: 1px solid rgba(25,118,210,0.06) !important;
    }
    .rbc-today{
      background: rgba(240,120,40,0.06) !important;
    }
  `;

  // ===== state (unchanged) =====
  const [events, setEvents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loginCampusCode, setLoginCampusCode] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("week");

  // Filters
  const [selectedSemester, setSelectedSemester] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubCampus, setSelectedSubCampus] = useState("");
  const [selectedDegree, setSelectedDegree] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState("");

  // pending filters (Apply/Reset UX)
  const [pendingSemester, setPendingSemester] = useState("");
  const [pendingSection, setPendingSection] = useState("");
  const [pendingSubCampus, setPendingSubCampus] = useState("");
  const [pendingDegree, setPendingDegree] = useState("");
  const [pendingBranch, setPendingBranch] = useState("");
  const [pendingTeacher, setPendingTeacher] = useState("");

  // Modal state for event creation
  const [showClassModal, setShowClassModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [recurrenceFrequency, setRecurrenceFrequency] = useState("WEEKLY");
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");

  const [modalStartDate, setModalStartDate] = useState("");
  const [modalStartTime, setModalStartTime] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(60);

  // Modal state for editing recurrence
  const [showEditModal, setShowEditModal] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [editRecurrenceFrequency, setEditRecurrenceFrequency] = useState("WEEKLY");
  const [editRecurrenceInterval, setEditRecurrenceInterval] = useState(1);
  const [editRecurrenceEndDate, setEditRecurrenceEndDate] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editStartTime, setEditStartTime] = useState("");
  const [editDurationMinutes, setEditDurationMinutes] = useState(60);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const token = localStorage.getItem("auth");
  const localizer = momentLocalizer(moment);

  // Fetch loginCampusCode from JWT
  useEffect(() => {
    if (!token) return;
    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setLoginCampusCode(decodedToken.campusCode);
    } catch (err) {
      console.error("Error decoding token:", err);
      setLoginCampusCode("");
    }
  }, [token]);

  // Fetch events and classes when loginCampusCode is available
  useEffect(() => {
    if (loginCampusCode) {
      fetchEvents();
      fetchClasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginCampusCode, success]);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/GetClassSchedule/${loginCampusCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const allEvents = data
        .map((event) => {
          if (event.RecurrenceRule) {
            const rule = RRule.fromString(event.RecurrenceRule);
            const occurrences = rule.all().map((date) => ({
              ...event,
              title: `${event.SubjectNameCode} (Sec:${event.Section})`,
              start: new Date(date),
              end: new Date(date.getTime() + (event.DurationMinutes || 60) * 60000),
            }));
            return occurrences;
          } else {
            const start = new Date(event.StartTime);
            const end = new Date(start.getTime() + (event.DurationMinutes || 60) * 60000);
            return {
              ...event,
              title: `${event.SubjectNameCode} (Sec:${event.Section})`,
              start,
              end,
            };
          }
        })
        .flat();
      setEvents(allEvents);
    } catch (e) {
      console.error("Error fetching events:", e);
      setError("Failed to fetch schedule");
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/GetClasses/${loginCampusCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setClasses(data);
    } catch (e) {
      console.error("Error fetching classes:", e);
      setError("Failed to fetch classes");
    }
  };

  // Prepare distinct filter options from classes
  const semesters = useMemo(() => {
    const semSet = new Set();
    classes.forEach((cls) => cls.Semester && semSet.add(cls.Semester));
    return Array.from(semSet);
  }, [classes]);

  const sections = useMemo(() => {
    const secSet = new Set();
    classes.forEach((cls) => cls.Section && secSet.add(cls.Section));
    return Array.from(secSet);
  }, [classes]);

  const subCampusCodes = useMemo(() => {
    const set = new Set();
    classes.forEach((cls) => cls.SubCampusCode && set.add(cls.SubCampusCode));
    return Array.from(set);
  }, [classes]);

  const degreeCodes = useMemo(() => {
    const set = new Set();
    classes.forEach((cls) => cls.DegreeCode && set.add(cls.DegreeCode));
    return Array.from(set);
  }, [classes]);

  const branchCodes = useMemo(() => {
    const set = new Set();
    classes.forEach((cls) => cls.BranchCode && set.add(cls.BranchCode));
    return Array.from(set);
  }, [classes]);

  const teacherNames = useMemo(() => {
    const set = new Set();
    classes.forEach((cls) => cls.TeacherName && set.add(cls.TeacherName));
    return Array.from(set);
  }, [classes]);

  // Filter classes by selected filters (for event creation)
  const filteredClasses = useMemo(() => {
    return classes.filter((cls) => {
      if (selectedSemester && cls.Semester !== selectedSemester) return false;
      if (selectedSection && cls.Section !== selectedSection) return false;
      if (selectedSubCampus && cls.SubCampusCode !== selectedSubCampus) return false;
      if (selectedDegree && cls.DegreeCode !== selectedDegree) return false;
      if (selectedBranch && cls.BranchCode !== selectedBranch) return false;
      if (selectedTeacher && cls.TeacherName !== selectedTeacher) return false;
      return true;
    });
  }, [classes, selectedSemester, selectedSection, selectedSubCampus, selectedDegree, selectedBranch, selectedTeacher]);

  // Filter events by selected filters (for calendar display)
  const filteredEvents = useMemo(() => {
    return events.filter((evt) => {
      if (selectedSemester && evt.Semester !== selectedSemester) return false;
      if (selectedSection && evt.Section !== selectedSection) return false;
      if (selectedSubCampus && evt.SubCampusCode !== selectedSubCampus) return false;
      if (selectedDegree && evt.DegreeCode !== selectedDegree) return false;
      if (selectedBranch && evt.BranchCode !== selectedBranch) return false;
      if (selectedTeacher && evt.TeacherName !== selectedTeacher) return false;
      return true;
    });
  }, [events, selectedSemester, selectedSection, selectedSubCampus, selectedDegree, selectedBranch, selectedTeacher]);

  useEffect(() => {
    setPendingSemester(selectedSemester);
    setPendingSection(selectedSection);
    setPendingSubCampus(selectedSubCampus);
    setPendingDegree(selectedDegree);
    setPendingBranch(selectedBranch);
    setPendingTeacher(selectedTeacher);
  }, [selectedSemester, selectedSection, selectedSubCampus, selectedDegree, selectedBranch, selectedTeacher, classes]);

  const handleSelectSlot = ({ start, end }) => {
    if (filteredClasses.length === 0) {
      window.alert("No classes available for the selected filters.");
      return;
    }
    setSelectedSlot({ start, end });
    setModalStartDate(moment(start).format("YYYY-MM-DD"));
    setModalStartTime(moment(start).format("HH:mm"));
    setDurationMinutes(Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000)) || 60);
    setShowClassModal(true);
  };

  const handleModalSubmit = () => {
    if (!recurrenceEndDate) {
      window.alert("Please select a Recurrence End Date before submitting.");
      return;
    }
    const selectedClass = filteredClasses.find((cls) => cls.ID === Number(selectedClassId));
    if (!selectedClass) {
      window.alert("Please select a valid class.");
      return;
    }

    const [y, m, d] = modalStartDate.split("-").map(Number);
    const [hh, mm] = modalStartTime.split(":").map(Number);
    const startDate = new Date(y, m - 1, d, hh, mm, 0, 0);

    const frequencyOptions = {
      DAILY: RRule.DAILY,
      WEEKLY: RRule.WEEKLY,
      MONTHLY: RRule.MONTHLY,
    };
    const freq = frequencyOptions[recurrenceFrequency.toUpperCase()] || RRule.WEEKLY;
    const intervalVal = parseInt(recurrenceInterval, 10) || 1;

    const ruleOptions = {
      freq,
      interval: intervalVal,
      dtstart: startDate,
      ...(recurrenceEndDate
        ? { until: moment(recurrenceEndDate, "YYYY-MM-DD").endOf("day").toDate() }
        : {}),
    };

    const rule = new RRule(ruleOptions);

    const newEvent = {
      ...selectedClass,
      start: startDate,
      end: new Date(startDate.getTime() + durationMinutes * 60000),
      title: `${selectedClass.SubjectNameCode} (Sec:${selectedClass.Section}) (${selectedClass.TeacherName})`,
      RecurrenceRule: rule.toString(),
      DurationMinutes: durationMinutes,
      Semester: selectedClass.Semester,
      SubCampusCode: selectedClass.SubCampusCode,
      DegreeCode: selectedClass.DegreeCode,
      BranchCode: selectedClass.BranchCode,
      TeacherName: selectedClass.TeacherName,
      ColorCode: selectedClass.ColorCode,
    };

    setEvents([...events, newEvent]);

    fetch(`${API_URL}/api/InsertClassSchedule`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        ClassID: selectedClass.ID,
        StartTime: moment(startDate).format("YYYY-MM-DD HH:mm:ss"),
        CampusCode: loginCampusCode,
        RecurrenceRule: rule.toString(),
        DurationMinutes: durationMinutes,
      }),
    })
      .then((res) => {
        if (res.ok) setSuccess("Created");
        else throw new Error("Failed to create event");
      })
      .catch((e) => {
        console.error("Error inserting class schedule:", e);
        setError("Failed to create schedule");
      });

    // Reset modal
    setShowClassModal(false);
    setSelectedSlot(null);
    setSelectedClassId("");
    setRecurrenceFrequency("WEEKLY");
    setRecurrenceInterval(1);
    setRecurrenceEndDate("");
    setModalStartDate("");
    setModalStartTime("");
    setDurationMinutes(60);
  };

  const handleEditRecurrence = (event) => {
    setEventToEdit(event);

    let untilDate = "";
    let freq = "WEEKLY";
    let interval = 1;

    if (event.RecurrenceRule) {
      try {
        const ruleObj = RRule.fromString(event.RecurrenceRule);
        freq = Object.keys(RRule).find((k) => RRule[k] === ruleObj.options.freq) || "WEEKLY";
        interval = ruleObj.options.interval || 1;
        if (ruleObj.options.until) untilDate = moment(ruleObj.options.until).format("YYYY-MM-DD");
      } catch (e) {
        console.error("Failed to parse RRULE:", e);
      }
    }

    setEditRecurrenceFrequency(freq);
    setEditRecurrenceInterval(interval);
    setEditRecurrenceEndDate(untilDate);
    setEditStartDate(moment(event.start).format("YYYY-MM-DD"));
    setEditStartTime(moment(event.start).format("HH:mm"));
    setEditDurationMinutes(event.DurationMinutes || 60);
    setShowEditModal(true);
  };

  const handleEditModalSubmit = () => {
    if (!editRecurrenceEndDate) {
      window.alert("Please select a Recurrence End Date before submitting.");
      return;
    }
    if (!eventToEdit) return;

    const frequencyOptions = {
      DAILY: RRule.DAILY,
      WEEKLY: RRule.WEEKLY,
      MONTHLY: RRule.MONTHLY,
    };
    const freq = frequencyOptions[editRecurrenceFrequency.toUpperCase()] || RRule.WEEKLY;
    const intervalVal = parseInt(editRecurrenceInterval, 10) || 1;
    const until = editRecurrenceEndDate ? new Date(editRecurrenceEndDate) : null;

    const [y, m, d] = editStartDate.split("-").map(Number);
    const [hh, mm] = editStartTime.split(":").map(Number);
    const newStartDate = new Date(y, m - 1, d, hh, mm, 0, 0);

    const rule = new RRule({
      freq,
      interval: intervalVal,
      dtstart: newStartDate,
      ...(until ? { until } : {}),
    });

    const updatedEvent = {
      ...eventToEdit,
      start: newStartDate,
      end: new Date(newStartDate.getTime() + editDurationMinutes * 60000),
      RecurrenceRule: rule.toString(),
      DurationMinutes: editDurationMinutes,
    };

    setEvents(events.map((evt) => (evt.ClassScheduleID === eventToEdit.ClassScheduleID ? updatedEvent : evt)));

    fetch(`${API_URL}/api/UpdateClassScheduleRecurrenceRule/${eventToEdit.ClassScheduleID}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        StartTime: moment(newStartDate).format("YYYY-MM-DD HH:mm:ss"),
        RecurrenceRule: rule.toString(),
        DurationMinutes: editDurationMinutes,
      }),
    })
      .then((res) => {
        if (res.ok) setSuccess("Updated");
        else throw new Error("Failed to update event");
      })
      .catch((e) => {
        console.error(e);
        setError("Error updating recurrence rule");
      });

    setShowEditModal(false);
    setEventToEdit(null);
    setEditRecurrenceFrequency("WEEKLY");
    setEditRecurrenceInterval(1);
    setEditRecurrenceEndDate("");
    setEditStartDate("");
    setEditStartTime("");
    setEditDurationMinutes(60);
  };

  function updateDTSTARTinRecurrenceRuleUTC(rruleStr, newDtStart) {
    const pad = (n) => n.toString().padStart(2, "0");
    const y = newDtStart.getUTCFullYear();
    const m = pad(newDtStart.getUTCMonth() + 1);
    const d = pad(newDtStart.getUTCDate());
    const h = pad(newDtStart.getUTCHours());
    const min = pad(newDtStart.getUTCMinutes());
    const s = pad(newDtStart.getUTCSeconds());
    const dtStr = `${y}${m}${d}T${h}${min}${s}Z`;

    if (/^DTSTART:/m.test(rruleStr)) return rruleStr.replace(/^DTSTART:(.+)$/m, `DTSTART:${dtStr}`);
    return `DTSTART:${dtStr}\n${rruleStr}`;
  }

  const handleEventDrag = async ({ event, start, end }) => {
    const newDurationMinutes = Math.round((end.getTime() - start.getTime()) / 60000);

    let updatedRecurrenceRule = event.RecurrenceRule;
    if (event.RecurrenceRule) {
      updatedRecurrenceRule = updateDTSTARTinRecurrenceRuleUTC(event.RecurrenceRule, start);
    }

    const updatedEvent = {
      ...event,
      start,
      end: new Date(start.getTime() + newDurationMinutes * 60000),
      DurationMinutes: newDurationMinutes,
      RecurrenceRule: updatedRecurrenceRule,
    };

    setEvents(events.map((evt) => (evt.ClassScheduleID === event.ClassScheduleID ? updatedEvent : evt)));

    fetch(`${API_URL}/api/UpdateClassSchedule/${event.ClassScheduleID}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        StartTime: moment(start).format("YYYY-MM-DDTHH:mm:ss"),
        RecurrenceRule: updatedRecurrenceRule,
        DurationMinutes: newDurationMinutes,
      }),
    })
      .then((res) => {
        if (res.ok) setSuccess("Updated");
        else throw new Error("Failed to update event");
      })
      .catch((e) => {
        console.error("Error updating class schedule:", e);
        setError("Error updating recurrence rule");
      });
  };

  const handleEventDelete = async (event) => {
    if (!window.confirm(`Delete event: ${event.title}?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/DeleteClassSchedule/${event.ClassScheduleID}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(`${res.status}: ${body}`);
      }

      setEvents((prev) => prev.filter((e) => e.ClassScheduleID !== event.ClassScheduleID));
      setSuccess("Deleted");
    } catch (err) {
      console.error(err);
      setError("Error deleting class schedule");
    }
  };

  const eventStyleGetter = (event) => {
    const backgroundColor = event.ColorCode || "#3174ad";
    return {
      style: {
        backgroundColor,
        borderRadius: 8,
        opacity: 0.92,
        color: "white",
        border: "0px",
        display: "block",
        boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
      },
    };
  };

  const handleColorChange = (classId, newColor) => {
    setClasses((prev) => prev.map((cls) => (cls.ID === classId ? { ...cls, ColorCode: newColor } : cls)));

    setEvents((prev) =>
      prev.map((evt) =>
        evt.ID === classId || evt.ClassID === classId ? { ...evt, ColorCode: newColor } : evt
      )
    );

    fetch(`${API_URL}/api/UpdateClassColor/${classId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ColorCode: newColor }),
    }).catch((err) => console.error("Error updating color:", err));
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <style>{calendarCss}</style>

        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Class Schedule Management
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Tip: Right-click an event to edit recurrence • Click event to delete
            </Typography>
          </Box>
          <Box />
        </Box>

        <Box sx={bodySx}>
          {/* Content split */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              height: { xs: "auto", md: "80vh" },
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {/* Sidebar */}
            <Paper variant="outlined" sx={{ ...sectionSx, width: { xs: "100%", md: 340 }, flexShrink: 0 }}>
              <Box sx={{ pl: 1 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                  Filters
                </Typography>
                <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

                <Stack spacing={1.5}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Semester</InputLabel>
                    <Select value={pendingSemester} label="Semester" onChange={(e) => setPendingSemester(e.target.value)}>
                      <MenuItem value="">All Semesters</MenuItem>
                      {semesters.map((sem) => (
                        <MenuItem key={sem} value={sem}>
                          {sem}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Section</InputLabel>
                    <Select value={pendingSection} label="Section" onChange={(e) => setPendingSection(e.target.value)}>
                      <MenuItem value="">All Sections</MenuItem>
                      {sections.map((sec) => (
                        <MenuItem key={sec} value={sec}>
                          {sec}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>SubCampus</InputLabel>
                    <Select
                      value={pendingSubCampus}
                      label="SubCampus"
                      onChange={(e) => setPendingSubCampus(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {subCampusCodes.map((code) => (
                        <MenuItem key={code} value={code}>
                          {code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Degree</InputLabel>
                    <Select value={pendingDegree} label="Degree" onChange={(e) => setPendingDegree(e.target.value)}>
                      <MenuItem value="">All</MenuItem>
                      {degreeCodes.map((code) => (
                        <MenuItem key={code} value={code}>
                          {code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Branch</InputLabel>
                    <Select value={pendingBranch} label="Branch" onChange={(e) => setPendingBranch(e.target.value)}>
                      <MenuItem value="">All</MenuItem>
                      {branchCodes.map((code) => (
                        <MenuItem key={code} value={code}>
                          {code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Teacher</InputLabel>
                    <Select value={pendingTeacher} label="Teacher" onChange={(e) => setPendingTeacher(e.target.value)}>
                      <MenuItem value="">All</MenuItem>
                      {teacherNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      sx={primaryBtnSx}
                      onClick={() => {
                        setSelectedSemester(pendingSemester);
                        setSelectedSection(pendingSection);
                        setSelectedSubCampus(pendingSubCampus);
                        setSelectedDegree(pendingDegree);
                        setSelectedBranch(pendingBranch);
                        setSelectedTeacher(pendingTeacher);
                      }}
                    >
                      Apply
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={outlinedBtnSx}
                      onClick={() => {
                        setPendingSemester("");
                        setPendingSection("");
                        setPendingSubCampus("");
                        setPendingDegree("");
                        setPendingBranch("");
                        setPendingTeacher("");
                        setSelectedSemester("");
                        setSelectedSection("");
                        setSelectedSubCampus("");
                        setSelectedDegree("");
                        setSelectedBranch("");
                        setSelectedTeacher("");
                      }}
                    >
                      Reset
                    </Button>
                  </Stack>

                  <Divider sx={{ borderColor: "rgba(25,118,210,0.12)" }} />

                  <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                    Classes (color)
                  </Typography>

                  <Box sx={{ maxHeight: 300, overflowY: "auto", pr: 0.5 }}>
                    {filteredClasses.map((cls, idx) => (
                      <Box
                        key={cls.ID}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          py: 0.9,
                          px: 1,
                          borderRadius: 2,
                          border: "1px dashed rgba(25,118,210,0.18)",
                          backgroundColor: idx % 2 === 0 ? ROW_ALT : "#fff",
                        }}
                      >
                        <Box sx={{ flex: 1, fontSize: 12, lineHeight: 1.25 }}>
                          <b>{cls.SubjectNameCode}</b> — {cls.TeacherName} (Sec:{cls.Section})
                        </Box>

                        {/* native color input, themed wrapper */}
                        <Box
                          sx={{
                            width: 38,
                            height: 28,
                            borderRadius: 2,
                            border: `1px solid ${BORDER}`,
                            overflow: "hidden",
                            backgroundColor: "#fff",
                            flexShrink: 0,
                          }}
                        >
                          <input
                            type="color"
                            value={cls.ColorCode || "#3174ad"}
                            onChange={(e) => handleColorChange(cls.ID, e.target.value)}
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                              background: "transparent",
                            }}
                          />
                        </Box>
                      </Box>
                    ))}

                    {filteredClasses.length === 0 && (
                      <Typography sx={{ mt: 1, fontSize: 12, opacity: 0.75 }}>
                        No classes found for these filters.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            {/* Calendar */}
            <Paper variant="outlined" sx={{ ...surfaceSx, flex: 1, p: 1 }}>
              <DnDCalendar
                localizer={localizer}
                events={filteredEvents}
                date={currentDate}
                view={view}
                onView={setView}
                onNavigate={(date) => setCurrentDate(date)}
                style={{ height: "100%" }}
                selectable
                step={5}
                timeslots={12}
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleEventDelete}
                onEventDrop={handleEventDrag}
                onEventResize={handleEventDrag}
                eventPropGetter={eventStyleGetter}
                components={{
                  event: (props) => <CustomEvent {...props} onEditRecurrence={handleEditRecurrence} />,
                }}
              />
            </Paper>
          </Box>

          {/* ===== Create modal (MUI Dialog) ===== */}
          <Dialog open={showClassModal} onClose={() => setShowClassModal(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
              Create Schedule • Class + Recurrence
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <FormControl fullWidth size="small" sx={inputSx}>
                  <InputLabel>Class</InputLabel>
                  <Select value={selectedClassId} label="Class" onChange={(e) => setSelectedClassId(e.target.value)}>
                    <MenuItem value="">Select Class</MenuItem>
                    {filteredClasses.map((cls) => (
                      <MenuItem key={cls.ID} value={cls.ID}>
                        {cls.SubjectNameCode} ({cls.TeacherName}) (Sec:{cls.Section})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Start Date"
                    type="date"
                    size="small"
                    value={modalStartDate}
                    onChange={(e) => setModalStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={inputSx}
                  />
                  <TextField
                    label="Start Time"
                    type="time"
                    size="small"
                    value={modalStartTime}
                    onChange={(e) => setModalStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={inputSx}
                  />
                </Stack>

                <TextField
                  label="Duration (minutes)"
                  type="number"
                  size="small"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(Number(e.target.value))}
                  inputProps={{ min: 1, max: 600 }}
                  fullWidth
                  sx={inputSx}
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Frequency</InputLabel>
                    <Select value={recurrenceFrequency} label="Frequency" onChange={(e) => setRecurrenceFrequency(e.target.value)}>
                      <MenuItem value="DAILY">DAILY</MenuItem>
                      <MenuItem value="WEEKLY">WEEKLY</MenuItem>
                      <MenuItem value="MONTHLY">MONTHLY</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Interval"
                    type="number"
                    size="small"
                    value={recurrenceInterval}
                    onChange={(e) => setRecurrenceInterval(e.target.value)}
                    inputProps={{ min: 1 }}
                    fullWidth
                    sx={inputSx}
                  />
                </Stack>

                <TextField
                  label="Recurrence End Date"
                  type="date"
                  size="small"
                  value={recurrenceEndDate}
                  onChange={(e) => setRecurrenceEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={inputSx}
                />

                <Typography sx={{ fontSize: 12, opacity: 0.75 }}>
                  Note: End date is inclusive (till end of day).
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button variant="outlined" sx={outlinedBtnSx} onClick={() => setShowClassModal(false)}>
                Cancel
              </Button>
              <Button variant="contained" sx={primaryBtnSx} onClick={handleModalSubmit}>
                Submit
              </Button>
            </DialogActions>
          </Dialog>

          {/* ===== Edit modal (MUI Dialog) ===== */}
          <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
              Edit Recurrence • {eventToEdit?.title || ""}
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Start Date"
                    type="date"
                    size="small"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={inputSx}
                  />
                  <TextField
                    label="Start Time"
                    type="time"
                    size="small"
                    value={editStartTime}
                    onChange={(e) => setEditStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={inputSx}
                  />
                </Stack>

                <TextField
                  label="Duration (minutes)"
                  type="number"
                  size="small"
                  value={editDurationMinutes}
                  onChange={(e) => setEditDurationMinutes(Number(e.target.value))}
                  inputProps={{ min: 1, max: 600 }}
                  fullWidth
                  sx={inputSx}
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={editRecurrenceFrequency}
                      label="Frequency"
                      onChange={(e) => setEditRecurrenceFrequency(e.target.value)}
                    >
                      <MenuItem value="DAILY">DAILY</MenuItem>
                      <MenuItem value="WEEKLY">WEEKLY</MenuItem>
                      <MenuItem value="MONTHLY">MONTHLY</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Interval"
                    type="number"
                    size="small"
                    value={editRecurrenceInterval}
                    onChange={(e) => setEditRecurrenceInterval(e.target.value)}
                    inputProps={{ min: 1 }}
                    fullWidth
                    sx={inputSx}
                  />
                </Stack>

                <TextField
                  label="Recurrence End Date"
                  type="date"
                  size="small"
                  value={editRecurrenceEndDate}
                  onChange={(e) => setEditRecurrenceEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={inputSx}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button variant="outlined" sx={outlinedBtnSx} onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button variant="contained" sx={primaryBtnSx} onClick={handleEditModalSubmit}>
                Submit
              </Button>
            </DialogActions>
          </Dialog>

          {/* Toasts */}
          <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError("")}>
            <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          </Snackbar>
          <Snackbar open={!!success} autoHideDuration={2500} onClose={() => setSuccess("")}>
            <Alert severity="success" sx={{ borderRadius: 2 }} onClose={() => setSuccess("")}>
              {success}
            </Alert>
          </Snackbar>
        </Box>
      </Paper>
    </Box>
  );
};

export default ClassScheduler;
